import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSeo } from "@/hooks/usePageSeo";

const CookiesPolicyPage = () => {
  const { language, url, onLanguageChange } = usePageSeo();

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Cookies Policy - ROC Worldwide Agency LLC</title>
        <meta name="description" content="Learn about how Luis ROC uses cookies and tracking technologies on our website." />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Cookies Policy - Luis ROC" />
        <meta property="og:description" content="Cookies Policy for ROC Worldwide Agency LLC website." />
        <meta property="og:url" content={url} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="section-padding">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="text-center">
                <h1 className="text-3xl lg:text-4xl font-bold mb-4 heading-gradient">
                  Cookies Policy
                </h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              <Card className="card-elegant">
                <CardContent className="p-8 space-y-6">
                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">What Are Cookies?</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Cookies are small text files that are stored on your computer or mobile device when you visit a website. 
                      They are widely used to make websites work more efficiently, as well as to provide information to website owners. 
                      Cookies help us understand how visitors interact with our website, enabling us to improve user experience and 
                      provide more relevant content.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">How We Use Cookies</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      ROC Worldwide Agency LLC uses cookies for several purposes:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>To remember your preferences and settings</li>
                      <li>To analyze how our website is used and improve performance</li>
                      <li>To provide personalized content and recommendations</li>
                      <li>To enable certain website features and functionality</li>
                      <li>To measure the effectiveness of our marketing campaigns</li>
                      <li>To prevent fraud and enhance security</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Types of Cookies We Use</h2>
                    
                    <div className="space-y-4">
                      <div className="border-l-4 border-primary pl-4">
                        <h3 className="text-xl font-semibold mb-2">Essential Cookies</h3>
                        <p className="text-muted-foreground">
                          These cookies are necessary for the website to function properly. They enable core functionality such as 
                          security, network management, and accessibility. You cannot opt-out of these cookies.
                        </p>
                      </div>

                      <div className="border-l-4 border-blue-500 pl-4">
                        <h3 className="text-xl font-semibold mb-2">Performance Cookies</h3>
                        <p className="text-muted-foreground">
                          These cookies collect information about how visitors use our website, such as which pages are visited most 
                          often and if users get error messages. This data helps us improve website performance.
                        </p>
                      </div>

                      <div className="border-l-4 border-green-500 pl-4">
                        <h3 className="text-xl font-semibold mb-2">Functional Cookies</h3>
                        <p className="text-muted-foreground">
                          These cookies allow the website to remember choices you make and provide enhanced, more personal features. 
                          They may be set by us or by third-party providers whose services we have added to our pages.
                        </p>
                      </div>

                      <div className="border-l-4 border-purple-500 pl-4">
                        <h3 className="text-xl font-semibold mb-2">Marketing Cookies</h3>
                        <p className="text-muted-foreground">
                          These cookies track your online activity to help advertisers deliver more relevant advertising or to limit 
                          how many times you see an ad. They may be used to build a profile of your interests and show you relevant 
                          adverts on other sites.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Third-Party Cookies</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Our website may also use third-party cookies from the following services:
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Google Analytics</h4>
                        <p className="text-sm text-muted-foreground">
                          Helps us understand how visitors interact with our website by collecting and reporting information anonymously.
                        </p>
                      </div>
                      
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Google Ads</h4>
                        <p className="text-sm text-muted-foreground">
                          Used for remarketing and conversion tracking to measure the effectiveness of our advertising campaigns.
                        </p>
                      </div>
                      
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Social Media Platforms</h4>
                        <p className="text-sm text-muted-foreground">
                          Cookies from LinkedIn, Facebook, and other platforms when you interact with social sharing buttons.
                        </p>
                      </div>
                      
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Marketing Tools</h4>
                        <p className="text-sm text-muted-foreground">
                          Various marketing automation and lead generation tools that help us provide better service.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Managing Your Cookie Preferences</h2>
                    
                    <h3 className="text-xl font-semibold mb-3">Browser Settings</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Most web browsers allow you to control cookies through their settings preferences. You can:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                      <li>Delete all cookies that are already on your computer</li>
                      <li>Set your browser to prevent cookies from being placed</li>
                      <li>Accept cookies from specific sites</li>
                      <li>Accept cookies only from the site you are currently on</li>
                    </ul>

                    <h3 className="text-xl font-semibold mb-3">Browser-Specific Instructions</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Google Chrome</h4>
                        <p className="text-sm text-muted-foreground">
                          Settings → Privacy and Security → Cookies and other site data
                        </p>
                      </div>
                      
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Mozilla Firefox</h4>
                        <p className="text-sm text-muted-foreground">
                          Options → Privacy & Security → Cookies and Site Data
                        </p>
                      </div>
                      
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Safari</h4>
                        <p className="text-sm text-muted-foreground">
                          Preferences → Privacy → Manage Website Data
                        </p>
                      </div>
                      
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Microsoft Edge</h4>
                        <p className="text-sm text-muted-foreground">
                          Settings → Cookies and Site Permissions → Cookies and Site Data
                        </p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Opt-Out Options</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      You can opt out of certain types of tracking:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>
                        <strong>Google Analytics:</strong> Use the 
                        <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                          Google Analytics Opt-out Browser Add-on
                        </a>
                      </li>
                      <li>
                        <strong>Google Ads:</strong> Visit 
                        <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                          Google Ad Settings
                        </a>
                      </li>
                      <li>
                        <strong>Network Advertising Initiative:</strong> Visit 
                        <a href="http://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                          NAI Opt-out Page
                        </a>
                      </li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Impact of Disabling Cookies</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Please note that disabling cookies may impact your experience on our website:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Some features may not work properly or at all</li>
                      <li>You may need to manually adjust settings each time you visit</li>
                      <li>Personalized content and recommendations may not be available</li>
                      <li>Our ability to improve the website based on usage data may be limited</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Updates to This Policy</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We may update this Cookies Policy from time to time to reflect changes in technology, legislation, or our 
                      business practices. We encourage you to review this policy periodically. The date of the last update is 
                      shown at the top of this page.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Contact Us</h2>
                    <div className="text-muted-foreground leading-relaxed">
                      <p className="mb-4">
                        If you have any questions about our use of cookies or this Cookies Policy, please contact us:
                      </p>
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <p className="mb-2">
                          <strong>ROC Worldwide Agency LLC</strong>
                        </p>
                        <p>9002 Six Pines Dr, Suite 277</p>
                        <p>Shenandoah, TX 77380, United States</p>
                        <p>Phone: (832) 521-5050</p>
                        <p>Email: luisroc@luisroc.com</p>
                      </div>
                    </div>
                  </section>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default CookiesPolicyPage;